import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Play,
  Trophy,
  Frown,
  Gift,
  RotateCcw,
  Users,
  BarChart3,
  Gamepad2,
  Loader2,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Badge } from '../components/ui/Badge.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Contest {
  id: string;
  programId: string;
  title: string;
  description: string | null;
  contestType: 'instant_win' | 'draw' | 'leaderboard';
  status: string;
  startDate: string;
  endDate: string;
  config: Record<string, unknown>;
}

interface ContestRound {
  id: string;
  contestId: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
}

interface Customer {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface PlayResult {
  outcome: 'win' | 'lose' | 'consolation';
  attemptId: string;
  prize?: { id: string; prizeType: string; prizeValue: Record<string, unknown> };
  consolationValue?: { points: number };
}

interface Attempt {
  id: string;
  customerId: string;
  roundId: string;
  outcome: 'win' | 'lose' | 'consolation';
  gameType: string | null;
  playedAt: string;
  prizeId: string | null;
}

// ─── Outcome animation ───────────────────────────────────────────────────────

const OUTCOME_CONFIG = {
  win: {
    icon: Trophy,
    label: 'YOU WIN!',
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    border: 'border-amber-400/30',
    glow: 'shadow-amber-400/20',
  },
  consolation: {
    icon: Gift,
    label: 'CONSOLATION PRIZE',
    color: 'text-violet-400',
    bg: 'bg-violet-400/10',
    border: 'border-violet-400/30',
    glow: 'shadow-violet-400/20',
  },
  lose: {
    icon: Frown,
    label: 'NO LUCK',
    color: 'text-zinc-400',
    bg: 'bg-zinc-500/10',
    border: 'border-zinc-500/30',
    glow: 'shadow-zinc-400/10',
  },
};

const GAME_TYPES = [
  { value: 'scratch_card', label: 'Scratch Card' },
  { value: 'spin_wheel', label: 'Spin Wheel' },
  { value: 'slot_machine', label: 'Slot Machine' },
  { value: 'gift_box', label: 'Gift Box' },
];

const CONTEST_TYPE_COLORS: Record<string, string> = {
  instant_win: 'bg-violet-100 text-violet-700',
  draw: 'bg-sky-100 text-sky-700',
  leaderboard: 'bg-amber-100 text-amber-700',
};

// ─── Component ───────────────────────────────────────────────────────────────

export function ContestTestArenaPage() {
  const { id: programId, contestId } = useParams<{ id: string; contestId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [selectedRoundId, setSelectedRoundId] = useState<string>('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedGameType, setSelectedGameType] = useState<string>('scratch_card');
  const [lastResult, setLastResult] = useState<PlayResult | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [stats, setStats] = useState({ plays: 0, wins: 0, losses: 0, consolations: 0 });

  // ── Data queries ────────────────────────────────────────────────────────────

  const { data: contest, isLoading: loadingContest } = useQuery<Contest>({
    queryKey: ['contest', contestId],
    queryFn: () => api.get(`/api/v1/loyalty/contests/${contestId}`).then((r) => r.data),
    enabled: !!contestId,
  });

  const { data: rounds = [] } = useQuery<ContestRound[]>({
    queryKey: ['contest-rounds', contestId],
    queryFn: () =>
      api.get(`/api/v1/loyalty/contests/${contestId}/rounds`).then((r) => r.data),
    enabled: !!contestId,
  });

  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers-list'],
    queryFn: () =>
      api.get('/api/v1/customers', { params: { limit: 50 } }).then((r) => {
        const d = r.data as Record<string, unknown>;
        return (d.items ?? d.data ?? d) as Customer[];
      }),
  });

  const { data: prizes = [] } = useQuery<{ id: string; name: string; prizeType: string }[]>({
    queryKey: ['contest-prizes', contestId, selectedRoundId],
    queryFn: () =>
      api
        .get(`/api/v1/loyalty/contests/${contestId}/rounds/${selectedRoundId}/prizes`)
        .then((r) => r.data),
    enabled: !!contestId && !!selectedRoundId,
  });

  const prizeMap = Object.fromEntries(prizes.map((p) => [p.id, p.name ?? p.prizeType]));

  const { data: attempts = [], refetch: refetchAttempts } = useQuery<Attempt[]>({
    queryKey: ['contest-attempts', contestId, selectedRoundId],
    queryFn: () =>
      api
        .get(`/api/v1/loyalty/contests/${contestId}/rounds/${selectedRoundId}/attempts`, {
          params: { limit: 50 },
        })
        .then((r) => r.data),
    enabled: !!contestId && !!selectedRoundId,
  });

  // Auto-select first round when loaded
  React.useEffect(() => {
    if (rounds.length > 0 && !selectedRoundId) {
      const active = rounds.find((r) => r.status === 'active');
      setSelectedRoundId(active?.id ?? rounds[0].id);
    }
  }, [rounds, selectedRoundId]);

  // ── Play mutation ───────────────────────────────────────────────────────────

  const playMutation = useMutation<PlayResult, Error>({
    mutationFn: () =>
      api
        .post(`/api/v1/loyalty/contests/${contestId}/test-play`, {
          roundId: selectedRoundId,
          customerId: selectedCustomerId,
          gameType: selectedGameType,
        })
        .then((r) => r.data),
    onSuccess: (result) => {
      setLastResult(result);
      setShowResult(true);
      setStats((s) => ({
        plays: s.plays + 1,
        wins: s.wins + (result.outcome === 'win' ? 1 : 0),
        losses: s.losses + (result.outcome === 'lose' ? 1 : 0),
        consolations: s.consolations + (result.outcome === 'consolation' ? 1 : 0),
      }));
      refetchAttempts();
      qc.invalidateQueries({ queryKey: ['contest-attempts'] });
    },
  });

  const selectedRound = rounds.find((r) => r.id === selectedRoundId);
  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);
  const roundNotActive = selectedRound && selectedRound.status !== 'active';
  const contestNotActive = contest && contest.status !== 'active';
  const canPlay = !!selectedRoundId && !!selectedCustomerId && !playMutation.isPending && !roundNotActive && !contestNotActive;

  const handlePlay = useCallback(() => {
    if (!canPlay) return;
    setShowResult(false);
    setLastResult(null);
    playMutation.mutate();
  }, [canPlay, playMutation]);

  const resetStats = useCallback(() => {
    setStats({ plays: 0, wins: 0, losses: 0, consolations: 0 });
    setLastResult(null);
    setShowResult(false);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loadingContest) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-zinc-400" size={24} />
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="p-8 text-center text-zinc-500">
        Contest not found.{' '}
        <Link to={`/loyalty/${programId}`} className="text-zinc-700 underline">
          Back to program
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/loyalty/${programId}`)}
          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Gamepad2 size={18} className="text-zinc-400" />
            <h1 className="text-lg font-semibold text-zinc-900 truncate">
              Test Arena
            </h1>
            <span className="text-zinc-300">|</span>
            <span className="text-sm text-zinc-500 truncate">{contest.title}</span>
            <span
              className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${CONTEST_TYPE_COLORS[contest.contestType] ?? 'bg-zinc-100 text-zinc-500'}`}
            >
              {contest.contestType.replace('_', ' ')}
            </span>
            <Badge
              variant={contest.status === 'active' ? 'success' : 'default'}
            >
              {contest.status}
            </Badge>
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">
            Play the contest as a customer to test the instant-win flow.
          </p>
        </div>
      </div>

      {/* ── Warning if not active ────────────────────────────────────────────── */}
      {contest.status !== 'active' && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="text-lg">⚠️</span>
          <span>
            This contest is <strong>{contest.status}</strong>. You must activate it before plays will succeed.
            The play API requires the contest to be in <strong>active</strong> status.
          </span>
        </div>
      )}

      {/* ── Configuration bar ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white border border-zinc-200 rounded-lg p-4">
        {/* Customer selector */}
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            <Users size={12} className="inline mr-1" />
            Customer
          </label>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="w-full text-sm border border-zinc-200 rounded-md px-2.5 py-1.5 bg-white text-zinc-800 focus:ring-1 focus:ring-zinc-400 focus:outline-none"
          >
            <option value="">Select a customer...</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email}
                {c.firstName || c.lastName ? ` (${c.email})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Round selector */}
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            <BarChart3 size={12} className="inline mr-1" />
            Round
          </label>
          <select
            value={selectedRoundId}
            onChange={(e) => setSelectedRoundId(e.target.value)}
            className="w-full text-sm border border-zinc-200 rounded-md px-2.5 py-1.5 bg-white text-zinc-800 focus:ring-1 focus:ring-zinc-400 focus:outline-none"
          >
            {rounds.length === 0 && <option value="">No rounds</option>}
            {rounds.map((r) => (
              <option key={r.id} value={r.id}>
                {r.status === 'active' ? '🟢 ' : r.status === 'completed' ? '⚫ ' : '🟡 '}{r.name} ({r.status})
              </option>
            ))}
          </select>
        </div>

        {/* Game type selector */}
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">
            <Gamepad2 size={12} className="inline mr-1" />
            Game Type
          </label>
          <select
            value={selectedGameType}
            onChange={(e) => setSelectedGameType(e.target.value)}
            className="w-full text-sm border border-zinc-200 rounded-md px-2.5 py-1.5 bg-white text-zinc-800 focus:ring-1 focus:ring-zinc-400 focus:outline-none"
          >
            {GAME_TYPES.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Game area + Stats ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Game card */}
        <div className="lg:col-span-2">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
            {/* Play area */}
            <div className="flex flex-col items-center justify-center py-16 px-8 min-h-[320px]">
              {playMutation.isPending ? (
                <div className="animate-pulse flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-zinc-700 animate-spin flex items-center justify-center">
                    <Gamepad2 size={32} className="text-zinc-400" />
                  </div>
                  <p className="text-zinc-400 text-sm">Playing...</p>
                </div>
              ) : showResult && lastResult ? (
                <ResultDisplay result={lastResult} />
              ) : (
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center mx-auto mb-4">
                    <Gamepad2 size={32} className="text-zinc-500" />
                  </div>
                  <p className="text-zinc-400 text-sm">
                    {!selectedCustomerId
                      ? 'Select a customer to start testing'
                      : !selectedRoundId
                        ? 'No active round available'
                        : 'Ready to play!'}
                  </p>
                </div>
              )}

              {/* Error display */}
              {playMutation.isError && (
                <div className="mt-4 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400 text-sm">
                    {extractError(playMutation.error)}
                  </p>
                </div>
              )}
            </div>

            {/* Play button bar */}
            <div className="border-t border-zinc-800 bg-zinc-900/80 px-6 py-4 flex items-center justify-between">
              <div className="text-xs text-zinc-500">
                {selectedCustomer && (
                  <span>
                    Playing as{' '}
                    <strong className="text-zinc-300">
                      {selectedCustomer.firstName ?? selectedCustomer.email}
                    </strong>
                    {selectedRound && (
                      <>
                        {' '}
                        in <strong className="text-zinc-300">{selectedRound.name}</strong>
                      </>
                    )}
                  </span>
                )}
              </div>
              <button
                onClick={handlePlay}
                disabled={!canPlay}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                <Play size={16} />
                PLAY
              </button>
            </div>
          </div>
        </div>

        {/* Stats panel */}
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-zinc-700">Session Stats</h3>
              <button
                onClick={resetStats}
                title="Reset stats"
                className="p-1 text-zinc-400 hover:text-zinc-600 rounded"
              >
                <RotateCcw size={13} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatBox label="Plays" value={stats.plays} color="text-zinc-700" />
              <StatBox label="Wins" value={stats.wins} color="text-amber-600" />
              <StatBox label="Losses" value={stats.losses} color="text-zinc-400" />
              <StatBox label="Consolations" value={stats.consolations} color="text-violet-600" />
            </div>
            {stats.plays > 0 && (
              <div className="mt-3 pt-3 border-t border-zinc-100">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-zinc-500">Win rate</span>
                  <span className="font-semibold text-zinc-700">
                    {((stats.wins / stats.plays) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Contest info */}
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-zinc-700 mb-2">Contest Info</h3>
            <dl className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <dt className="text-zinc-400">Type</dt>
                <dd className="text-zinc-700 capitalize">
                  {contest.contestType.replace('_', ' ')}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-zinc-400">Status</dt>
                <dd className="text-zinc-700 capitalize">{contest.status}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-zinc-400">Dates</dt>
                <dd className="text-zinc-700">
                  {new Date(contest.startDate).toLocaleDateString()} &ndash;{' '}
                  {new Date(contest.endDate).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-zinc-400">Rounds</dt>
                <dd className="text-zinc-700">{rounds.length}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* ── Attempt history ────────────────────────────────────────────────────── */}
      {selectedRoundId && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-sm font-medium text-zinc-700">
              Recent Attempts ({attempts.length})
            </h3>
          </div>
          {attempts.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-400">
              No attempts in this round yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 text-left text-xs text-zinc-500">
                    <th className="px-4 py-2 font-medium">Outcome</th>
                    <th className="px-4 py-2 font-medium">Customer</th>
                    <th className="px-4 py-2 font-medium">Game Type</th>
                    <th className="px-4 py-2 font-medium">Played At</th>
                    <th className="px-4 py-2 font-medium">Prize</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {[...attempts].reverse().slice(0, 30).map((a) => {
                    const cust = customers.find((c) => c.id === a.customerId);
                    return (
                      <tr key={a.id} className="hover:bg-zinc-50/50">
                        <td className="px-4 py-2">
                          <OutcomeBadge outcome={a.outcome} />
                        </td>
                        <td className="px-4 py-2 text-zinc-600">
                          {cust
                            ? [cust.firstName, cust.lastName].filter(Boolean).join(' ') ||
                              cust.email
                            : a.customerId.slice(0, 8)}
                        </td>
                        <td className="px-4 py-2 text-zinc-500">
                          {a.gameType?.replace('_', ' ') ?? '-'}
                        </td>
                        <td className="px-4 py-2 text-zinc-400">
                          {new Date(a.playedAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-zinc-500">
                          {a.prizeId ? (prizeMap[a.prizeId] ?? a.prizeId.slice(0, 8) + '...') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ResultDisplay({ result }: { result: PlayResult }) {
  const cfg = OUTCOME_CONFIG[result.outcome];
  const Icon = cfg.icon;

  return (
    <div
      className={`flex flex-col items-center gap-4 animate-in fade-in zoom-in-90 duration-300`}
    >
      <div
        className={`w-24 h-24 rounded-full ${cfg.bg} border-2 ${cfg.border} flex items-center justify-center shadow-lg ${cfg.glow}`}
      >
        <Icon size={40} className={cfg.color} />
      </div>
      <p className={`text-2xl font-bold ${cfg.color} tracking-wider`}>{cfg.label}</p>
      {result.prize && (
        <div className="text-center">
          <p className="text-sm text-zinc-300">
            Prize: <strong className="text-amber-300">{result.prize.prizeType}</strong>
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">
            {JSON.stringify(result.prize.prizeValue)}
          </p>
        </div>
      )}
      {result.consolationValue && (
        <p className="text-sm text-violet-300">
          +{result.consolationValue.points} points
        </p>
      )}
    </div>
  );
}

function StatBox({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="text-center p-2 bg-zinc-50 rounded-lg">
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</p>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const styles: Record<string, string> = {
    win: 'bg-amber-100 text-amber-700',
    lose: 'bg-zinc-100 text-zinc-500',
    consolation: 'bg-violet-100 text-violet-700',
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full capitalize ${styles[outcome] ?? 'bg-zinc-100 text-zinc-500'}`}
    >
      {outcome}
    </span>
  );
}
